import { type MouseEventHandler, type PropsWithChildren } from 'react';
import styled from 'styled-components';

import { Skeleton, SkeletonItem } from '../../atoms';

import type { AllOrNever } from '@capsicum/common';

export type HeaderBlockProps = PropsWithChildren<
	{
		isLoading?: boolean;
		rightText?: string;
		testID: string;
		title?: string;
	} & AllOrNever<{ actionText: string; onActionClick?: MouseEventHandler<HTMLButtonElement> }>
>;

export const HeaderBlock = ({
	actionText,
	children,
	isLoading,
	onActionClick,
	rightText,
	testID,
	title,
	...props
}: HeaderBlockProps) => {
	return (
		<S.Block {...props}>
			<S.Header>
				{title && (
					<S.HeaderTitle data-testid={`${testID}-header-title`}>
						{isLoading ? (
							<Skeleton>
								<SkeletonItem height={25} width={150} />
							</Skeleton>
						) : (
							title
						)}
					</S.HeaderTitle>
				)}
				{rightText && (
					<S.HeaderRightText data-testid={`${testID}-header-rightText`}>
						{isLoading ? (
							<Skeleton>
								<SkeletonItem height={25} width={50} />
							</Skeleton>
						) : (
							rightText
						)}
					</S.HeaderRightText>
				)}
				{onActionClick && (
					<S.HeaderMainActionBtn data-testid={`${testID}-header-main-link`} onClick={onActionClick}>
						{isLoading ? (
							<Skeleton>
								<Skeleton height={25} width={50} />
							</Skeleton>
						) : (
							actionText
						)}
					</S.HeaderMainActionBtn>
				)}
			</S.Header>
			{children && <S.Content>{children}</S.Content>}
		</S.Block>
	);
};

namespace S {
	export const Block = styled.div`
		display: flex;
		flex-direction: column;
	`;

	export const Header = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const HeaderTitle = styled.div`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title5};
		word-break: break-word;
		flex-shrink: 1;
	`;

	export const HeaderRightText = styled.div`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title6};
		word-break: break-word;
		flex-shrink: 1;
		align-self: center;
	`;

	export const HeaderMainActionBtn = styled.button`
		${({ theme }) => theme.fonts.text.text1};
		margin: ${({ theme }) => `${theme.spacings.xxxsmall} 0 0 ${theme.spacings.small}`};
		padding: 0;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		text-decoration: none;
		border: none;
		background: ${({ theme }) => theme.colors.backgrounds.transparent};
		flex-shrink: 0;
	`;

	export const Content = styled.div`
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: space-between;
	`;
}
